'use client';

import { useTranslations } from 'next-intl';
import { ReactNode, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import TitleBar from '../title-bar';
import clsx from 'clsx';
import styles from './index.module.css';
interface EmblaCarouselProps {
  title: string;
  rightTitle?: string;
  children: ReactNode[];
  colors?: Array<string>;
  rows?: number;
  columns?: 2 | 3 | 4;
  perView?: number;
  className?: string;
  showViewAll?: boolean;
  onClickViewAll?: () => void;
}

export default function EmblaCarousel({
  title,
  children,
  rows = 1,
  columns = 3,
  // 右边剩余的宽度，为了看到下一个
  perView = 0.8,
  className = '',
  showViewAll = false,
  rightTitle = '',
  colors = ['#FFDA46', '#DF8C00'],
  onClickViewAll,
}: EmblaCarouselProps) {
  const t = useTranslations('home');
  // 创建View All按钮组件
  const ViewAllButton = () => (
    <div
      className={clsx(styles.bgLast, rows === 1 && 'h-full', rows !== 1 && 'aspect-[130/171]', 'flex items-center justify-center w-full rounded-lg text-secondary text-[13px] active:scale-90')}
      onClick={onClickViewAll}
    >
      {t('viewAll')}
    </div>
  );

  const totalItems = children.length + (showViewAll ? 1 : 0);

  // 配置 Embla Carousel，包含滑动速度控制
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    dragFree: true,
    containScroll: 'trimSnaps',
    slidesToScroll: 1, // 始终滑动1个项目，保持一致性
  });

  // 滑动到下一组
  const scrollNextGroup = useCallback(() => {
    if (emblaApi) {
      // 计算需要滑动的次数来达到整屏切换
      const slidesPerScreen = Math.ceil(totalItems / rows);
      const currentIndex = emblaApi.selectedScrollSnap();
      const currentScreenItems = Math.min(columns, slidesPerScreen - currentIndex);

      // 使用scrollNext来保持跟触摸滑动一样的速度
      for (let i = 0; i < currentScreenItems; i++) {
        emblaApi.scrollNext();
      }
    }
  }, [emblaApi, columns, totalItems, rows]);

  // 滑动到上一组
  const scrollPrevGroup = useCallback(() => {
    if (emblaApi) {
      const currentIndex = emblaApi.selectedScrollSnap();
      const currentScreenItems = Math.min(columns, currentIndex + 1);

      // 使用scrollPrev来保持跟触摸滑动一样的速度
      for (let i = 0; i < currentScreenItems; i++) {
        emblaApi.scrollPrev();
      }
    }
  }, [emblaApi, columns, rows]);

  return (
    <div className={`w-full ${className}`}>
      {/* Header Section */}
      <TitleBar
        colors={colors}
        leftTitle={title}
        rightTitle={rightTitle || ''}
        onClickRightTitle={onClickViewAll}
      />

      {/* Content Container with Embla Carousel */}
      <div className="relative overflow-hidden w-full">
        <div className={clsx(styles.embla, 'w-full no-scrollbar')} ref={emblaRef} data-swipeable>
          <div className={clsx(styles.embla__container, 'w-full')}>
            {/* 每个slide包含rows个元素 */}
            {Array.from({ length: Math.ceil(totalItems / rows) }, (_, sliderIndex) => {
              const startIndex = sliderIndex * rows;
              const endIndex = Math.min(startIndex + rows, totalItems);
              const isLast = sliderIndex === Math.ceil(totalItems / rows) - 1;
              // 创建当前slide的项目数组
              const sliderItems: React.ReactElement[] = [];

              // 添加普通children
              const childrenStartIndex = startIndex;
              const childrenEndIndex = Math.min(endIndex, children.length);
              sliderItems.push(...children.slice(childrenStartIndex, childrenEndIndex).map((child, index) => (
                <div key={`child-${childrenStartIndex + index}`} className="w-full h-full">
                  {child}
                </div>
              )));

              // 如果当前slide还有空间且需要显示View All按钮，则添加
              if (showViewAll && sliderItems.length < rows && childrenStartIndex + sliderItems.length >= children.length) {
                sliderItems.push(
                  <div key="view-all" className={clsx("w-full h-full")}>
                    <ViewAllButton />
                  </div>
                );
              }

              const slideWidth = `${100 / (columns + perView)}%`;

              return (
                <div
                  key={sliderIndex}
                  className={`${styles.embla__slide} flex-shrink-0`}
                  style={{
                    // 根据columns和perView计算slide宽度
                    flex: `0 0 ${slideWidth}`,
                    maxWidth: slideWidth,
                    marginRight: isLast ? '0' : '10px'
                  }}
                >
                  <div className="flex flex-col w-full h-full space-y-[10px]">
                    {sliderItems}
                  </div>
                </div>
              );
            })}
            <div className="w-[12px] h-[100px] flex-shrink-0"></div>
          </div>
        </div>

        {/* 导航按钮 */}
        {false && (
          <>
            <button
              className={`${styles.embla__prev} absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors`}
              onClick={scrollPrevGroup}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              className={`${styles.embla__next} absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors`}
              onClick={scrollNextGroup}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
